import './JobInfo.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ProgressBar } from '../../../../__legacy__/components/ProgressBar';
import { EnableFor } from '../../../../__legacy__/containers/EnableForRole';
import { formatSecondsToTime, toLocaleDateTimeString } from '../../../../__legacy__/helpers/date.helpers';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { SpaceBetweenLayout } from '../../../common/layouts/spaceBetweenLayout/SpaceBetweenLayout';
import { JobStatusBadge } from '../../../common/statusesBadges/modelStatus/JobStatusBadge';
import { IJobDetailsStore } from '../../jobDetails.store';
import { useJobPermissions } from '../../jobPermissions';
import { IJobStartingProgress, JOB_STATUS, JOB_TYPE, TRAINING_TYPE } from '../../jobs.model';
import { getJobTranslationPrefix } from '../../utils';

interface IJobInfoProps {
  job: IJobDetailsStore['job'];
  jobType: JOB_TYPE;
}

export const JobInfo = observer((props: IJobInfoProps): ReactElement => {
  const permissions = useJobPermissions();
  const { t } = useTranslator();
  const {
    job: { status, dateCreated, startingProgress, failureReason, calculatedCost, isOwner, username, trainingTime, type },
    jobType,
  } = props;
  const translationPrefix = getJobTranslationPrefix(jobType);
  const usedCredits = Math.ceil(calculatedCost * 100);

  const getProgressBar = (startingProgress: IJobStartingProgress): ReactElement => {
    let progress = 0;
    switch (startingProgress) {
      case '1/4':
        progress = 25;
        break;
      case '2/4':
        progress = 50;
        break;
      case '3/4':
        progress = 75;
        break;
      case undefined:
      case null:
        break;
    }

    return (
      <ProgressBar
        value={progress}
        enableStripesAnimation
      />
    );
  };

  const renderStatus = (): ReactElement => {
    if (
      jobType === JOB_TYPE.TRAINING &&
      type === TRAINING_TYPE.AUTO &&
      status === JOB_STATUS.STARTING &&
      (startingProgress === '2/4' || startingProgress === '3/4')
    ) {
      return (
        <div className='status-with-progress'>
          <JobStatusBadge jobStatus={JOB_STATUS.RUNNING} />
        </div>
      );
    }

    return (
      <>
        <JobStatusBadge
          jobStatus={status}
          failureReason={failureReason ?? undefined}
        />
        {status === JOB_STATUS.STARTING && getProgressBar(startingProgress)}
      </>
    );
  };

  return (
    <S_Card className='job-info'>
      <ColumnLayout size='md'>
        <div className='job-header'>
          <h5>{t(`${translationPrefix}.information`)}</h5>
        </div>
        {status !== JOB_STATUS.UNKNOWN && (
          <ColumnLayout
            size='md'
            className='job-content'
          >
            <SpaceBetweenLayout>
              <div>{t('status')}</div>
              <ColumnLayout size='sm'>{renderStatus()}</ColumnLayout>
            </SpaceBetweenLayout>
            <SpaceBetweenLayout>
              <div>{t('createdDate')}</div>
              <div>{!dateCreated ? t('na') : toLocaleDateTimeString(dateCreated)}</div>
            </SpaceBetweenLayout>
            <SpaceBetweenLayout>
              <div>{t('createdBy')}</div>
              <div>{!username ? t('na') : username}</div>
            </SpaceBetweenLayout>
            {jobType === JOB_TYPE.TRAINING && (
              <SpaceBetweenLayout>
                <div>{t('training_time')}</div>
                <div>{!trainingTime ? t('na') : formatSecondsToTime(trainingTime)}</div>
              </SpaceBetweenLayout>
            )}
            <EnableFor
              hasPermission={permissions.canReadCostList(isOwner)}
              hideWhenNoAccess
            >
              <SpaceBetweenLayout>
                <div>{t('cloud_costs.calculated_cost')}</div>
                <div>{!usedCredits ? t('na') : `${usedCredits} ${usedCredits > 1 ? t('credits') : t('credit')}`}</div>
              </SpaceBetweenLayout>
            </EnableFor>
          </ColumnLayout>
        )}
      </ColumnLayout>
    </S_Card>
  );
});
